const SDK_SCRIPT_ID = "facebook-jssdk";
const SDK_SRC = "https://connect.facebook.net/en_US/sdk.js";
const SDK_TIMEOUT_MS = 10000;

let sdkPromise = null;

/**
 * Carga el SDK de Facebook una sola vez y resuelve con el objeto global.
 *
 * El SDK avisa que está listo por `window.fbAsyncInit`, así que la promesa se
 * guarda para que varias llamadas compartan la misma carga.
 *
 * @returns {Promise<object>} El objeto `window.FB` inicializado.
 */
export function loadFacebookSdk() {
  if (window.FB) return Promise.resolve(window.FB);
  if (sdkPromise) return sdkPromise;

  sdkPromise = new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      sdkPromise = null;
      document.getElementById(SDK_SCRIPT_ID)?.remove();
      reject(new Error("El SDK de Facebook tardó demasiado en cargar."));
    }, SDK_TIMEOUT_MS);

    const finish = () => {
      window.clearTimeout(timeoutId);
      resolve(window.FB);
    };

    const previousInit = window.fbAsyncInit;
    window.fbAsyncInit = () => {
      if (typeof previousInit === "function") previousInit();
      finish();
    };

    if (document.getElementById(SDK_SCRIPT_ID)) {
      if (window.FB) finish();
      return;
    }

    const script = document.createElement("script");
    script.id = SDK_SCRIPT_ID;
    script.async = true;
    script.defer = true;
    script.crossOrigin = "anonymous";
    script.src = SDK_SRC;
    script.onerror = () => {
      window.clearTimeout(timeoutId);
      sdkPromise = null;
      script.remove();
      reject(new Error("No se pudo cargar el SDK de Facebook."));
    };
    document.body.appendChild(script);
  });

  return sdkPromise;
}

const EMBEDDED_SIGNUP_MESSAGE_TYPE = "WA_EMBEDDED_SIGNUP";
export const STATE_MISMATCH_MESSAGE =
  "La respuesta de Meta no corresponde a esta conexión. Probá conectar de nuevo.";

const FACEBOOK_HOSTNAME = "facebook.com";

const isFacebookOrigin = (origin) => {
  try {
    const { protocol, hostname } = new URL(String(origin || ""));
    return (
      protocol === "https:" &&
      (hostname === FACEBOOK_HOSTNAME || hostname.endsWith(`.${FACEBOOK_HOSTNAME}`))
    );
  } catch {
    return false;
  }
};

const parseEmbeddedSignupMessage = (event) => {
  if (!isFacebookOrigin(event?.origin)) return null;
  let payload = event.data;
  if (typeof payload === "string") {
    try {
      payload = JSON.parse(payload);
    } catch {
      return null;
    }
  }
  return payload?.type === EMBEDDED_SIGNUP_MESSAGE_TYPE ? payload : null;
};

/**
 * Abre el Embedded Signup de Meta y devuelve el código de autorización.
 *
 * @param {object} params Datos del diálogo.
 * @param {object} params.sdk Objeto `window.FB` ya cargado.
 * @param {string} params.appId Id de la aplicación de Meta.
 * @param {string} params.configId Configuración del Embedded Signup.
 * @param {string} params.graphVersion Versión de la Graph API.
 * @param {string} params.state Valor que el backend espera de vuelta.
 * @param {string} [params.featureType] Flujo especial del diálogo; con
 *   "whatsapp_business_app_onboarding" se conecta un número que ya usa la app
 *   WhatsApp Business del teléfono y la sigue usando (coexistencia).
 * @returns {Promise<{code: string, state: string, wabaId: ?string, phoneNumberId: ?string}>}
 *   Código y state devueltos, más la WABA y el número que el usuario eligió en
 *   el diálogo (los informa Meta por postMessage; null si no llegaron).
 */
export function openEmbeddedSignup({
  sdk,
  appId,
  configId,
  graphVersion,
  state,
  featureType = "",
}) {
  sdk.init({
    appId,
    cookie: true,
    xfbml: false,
    version: graphVersion,
  });

  return new Promise((resolve, reject) => {
    let sessionInfo = null;
    const captureSessionInfo = (event) => {
      const payload = parseEmbeddedSignupMessage(event);
      if (payload?.data) sessionInfo = payload.data;
    };
    window.addEventListener("message", captureSessionInfo);

    sdk.login(
      (response) => {
        window.removeEventListener("message", captureSessionInfo);
        const authResponse = response?.authResponse;
        if (!authResponse?.code) {
          reject(new Error("No autorizaste la conexión con WhatsApp."));
          return;
        }
        if (authResponse.state && authResponse.state !== state) {
          reject(new Error(STATE_MISMATCH_MESSAGE));
          return;
        }
        resolve({
          code: authResponse.code,
          state,
          wabaId: sessionInfo?.waba_id ?? null,
          phoneNumberId: sessionInfo?.phone_number_id ?? null,
        });
      },
      {
        config_id: configId,
        response_type: "code",
        override_default_response_type: true,
        state,
        extras: {
          setup: {},
          ...(featureType ? { featureType } : {}),
        },
      },
    );
  });
}

/**
 * Olvida la carga en curso del SDK. Pensado para los tests.
 */
export function resetFacebookSdkForTests() {
  sdkPromise = null;
}
